import { type MouseEvent } from 'react';
import { Button, type ButtonProps } from '@zep-us/zus';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useUser } from '@/lib/auth';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { type EventProperties } from '@/utils/analytics';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { cn } from '@/utils/tailwind.util';
import { MyMenuDropdown } from './MyMenuDropdown';
import { trackGnbAnalytics } from './trackGnbAnalytics';
type HeaderButtonSize = NonNullable<ButtonProps['size']>;
const LOGIN_PATH = '/login';

/** 새 탭/창 열기(cmd·ctrl·shift·중클릭)는 브라우저 기본 동작에 맡긴다. */
const isModifiedClick = (event: MouseEvent<HTMLAnchorElement>) => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
type HeaderButtonProps = {
  type: 'drawer' | 'normal';
  className?: string;
  size?: HeaderButtonSize;
  trackingLocation?: string;
};
export const AuthButton = ({
  size,
  triggerSize
}: Pick<HeaderButtonProps, 'size'> & {
  triggerSize?: number;
} = {}) => {
  const {
    user,
    isInitialized
  } = useUser();
  if (!isInitialized) {
    return null;
  }
  if (user) {
    return <MyMenuDropdown className="hidden tablet-sm:flex" triggerSize={triggerSize} />;
  }
  return <LogInButton type="normal" size={size} className="hidden tablet-sm:flex" data-sentry-element="LogInButton" data-sentry-component="AuthButton" data-sentry-source-file="UserManagementButton.tsx" />;
};
export const LogInButton = ({
  type,
  className,
  size,
  trackingLocation = 'Header'
}: HeaderButtonProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const trackLogin = () => {
    trackGnbAnalytics('login', trackingLocation, isInGoogleClassroom ? {
      extension: 'google_classroom'
    } : undefined);
  };

  // href 는 `/login` 고정, callbackUrl 은 클릭 시점에만 붙인다.
  // href 에 페이지별 값을 박으면 크롤러가 페이지 수만큼 고유 login URL 을 수집한다.
  const handleLoginClick = (event: MouseEvent<HTMLAnchorElement>) => {
    trackLogin();
    if (isModifiedClick(event)) return;
    event.preventDefault();
    void router.push(combinePathAndQuery(LOGIN_PATH, {
      callbackUrl: router.asPath
    }));
  };
  if (type === 'drawer') {
    return <Button intent={'neutral'} asChild className={cn('border-transparent bg-transparent text-[15px] flex gap-3xs justify-start h-[48px]')}>
        <Link href={LOGIN_PATH} onClick={handleLoginClick}>
          <img src="/assets/icon_login.svg" alt="login-icon" />
          {t('header.login')}
        </Link>
      </Button>;
  }

  // 통합 로그인 버튼 - Auth Server SSO로 이동
  if (type === 'normal') {
    return <Button size={size ?? 'md'} variant={'transparent'} intent={'neutral'} asChild className={className}>
        <Link href={LOGIN_PATH} onClick={handleLoginClick}>
          {t('header.login')}
        </Link>
      </Button>;
  }
  return <Button size={size ?? 'md'} intent={'neutral'} variant={'transparent'} asChild className={cn(['border-transparent bg-transparent', className])} data-sentry-element="Button" data-sentry-component="LogInButton" data-sentry-source-file="UserManagementButton.tsx">
      <Link href={LOGIN_PATH} onClick={handleLoginClick} data-sentry-element="Link" data-sentry-source-file="UserManagementButton.tsx">
        {t('header.login')}
      </Link>
    </Button>;
};
export const LogOutButton = ({
  isDrawerOpen
}: {
  isDrawerOpen?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    logOut
  } = useUser();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const trackLogout = () => {
    trackGnbAnalytics('logout', 'Header', isInGoogleClassroom ? {
      extension: 'google_classroom'
    } : undefined);
  };
  if (isDrawerOpen) {
    return <Button intent={'neutral'} variant={'transparent'} size={'xs'} onClick={() => {
      void logOut({
        redirectTo: '/landing'
      });
      trackLogout();
    }}>
        {t('header.logout')}
      </Button>;
  }
  return <Button onClick={() => {
    void logOut({
      redirectTo: '/landing'
    });
    trackLogout();
  }} data-sentry-element="Button" data-sentry-component="LogOutButton" data-sentry-source-file="UserManagementButton.tsx">
      <img src="/assets/icon_out_red.svg" alt="logout" />{' '}
      <div className="text-[#FF4848]">{t('header.logout')}</div>
    </Button>;
};
export const CreateNewQuizButton = ({
  type
}: HeaderButtonProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    user
  } = useUser();
  const router = useRouter();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const trackCreate = () => {
    const eventProperties: EventProperties = {
      name: user?.username || ''
    };
    if (isInGoogleClassroom) {
      eventProperties.extension = 'google_classroom';
    }
    trackGnbAnalytics('create', 'Header', eventProperties);
  };
  const handleClick = () => {
    setReferrer({
      from: router.asPath,
      location: 'header_create_button'
    });
    trackCreate();
  };
  if (type === 'drawer') {
    return <Button className={'w-full'} intent={'alternative'} asChild>
        <Link href="/build" onClick={handleClick}>
          <img src="/assets/icon_plus_primary.png" className="size-[24px]" alt="plusicon" />
          {/* TODO: 퀴즈 만들기 */}
          {t('header.create.button')}
        </Link>
      </Button>;
  }
  return <Button size={'md'} asChild data-sentry-element="Button" data-sentry-component="CreateNewQuizButton" data-sentry-source-file="UserManagementButton.tsx">
      <Link href="/build" onClick={handleClick} className="flex gap-3xs px-xs py-[10px]" data-sentry-element="Link" data-sentry-source-file="UserManagementButton.tsx">
        <img src="/assets/icon_plus.png" className="size-[18px]" alt="plusicon" />
        {t('header.create.button')}
      </Link>
    </Button>;
};